import { getFieldLogic } from "@smartsuite/fields-logic/lib/field-type-registry/fields-logic-registry";
import type { AppField, Preferences } from "@smartsuite/types";
import { format } from "date-fns";
import { formatInTimeZone } from "date-fns-tz";

type UnknownRecord = Record<string, unknown>;

const isRecord = (value: unknown): value is UnknownRecord =>
  !!value && typeof value === "object" && !Array.isArray(value);

const getNestedValue = (value: unknown, key: string): unknown => {
  if (!isRecord(value)) return undefined;
  return value[key];
};

export const normalizeReviewReadableValue = (value: unknown): string => {
  if (value === null || value === undefined) return "";
  if (typeof value === "string") return value.trim();
  if (typeof value === "number" || typeof value === "boolean") return String(value);
  if (Array.isArray(value)) {
    return value
      .map((entry) => normalizeReviewReadableValue(entry))
      .filter(Boolean)
      .join(", ");
  }
  if (!isRecord(value)) return "";

  const preferredKeys = [
    "title",
    "label",
    "name",
    "display_name",
    "filename",
    "sys_title",
    "value",
    "address",
  ];

  for (const key of preferredKeys) {
    const nested = value[key];
    if (typeof nested === "string" || typeof nested === "number") {
      return String(nested);
    }
  }

  return Object.values(value)
    .map((entry) => normalizeReviewReadableValue(entry))
    .filter(Boolean)
    .join(", ");
};

export const defaultToReviewString = (
  value: unknown,
  appField: AppField,
  preferences?: Preferences,
): string => {
  const fieldLogic = getFieldLogic(appField.field_type);
  if (!fieldLogic) return normalizeReviewReadableValue(value);

  try {
    const displayValue = fieldLogic.getAggregateDisplayValue(value, appField, preferences);
    return displayValue || normalizeReviewReadableValue(value);
  } catch {
    return normalizeReviewReadableValue(value);
  }
};

export const smartdocToReviewString = (value: unknown): string => {
  if (!isRecord(value) || !("data" in value)) return "";

  const getNodeText = (node: unknown): string => {
    if (!isRecord(node)) return "";
    const text = typeof node.text === "string" ? node.text : "";
    const childText = Array.isArray(node.content)
      ? node.content.map((child) => getNodeText(child)).join(" ")
      : "";

    return [text, childText].filter(Boolean).join(" ").trim();
  };

  return getNodeText(value.data).replaceAll(/\s+/g, " ").trim();
};

/**
 * Single-line summary of a checklist: the item texts, then how many are ticked.
 *
 * The platform's own `valueToString` is unusable here — it embeds each item's raw
 * `content.html`, and the review page prints its value as plain text inside a `<dd>`, so the
 * markup would show up literally. Item text therefore goes through the smartdoc walker, and
 * the "(done/total)" suffix is digits only, so it needs no translated copy.
 */
export const checklistToReviewString = (value: unknown): string => {
  if (!isRecord(value)) return "";

  const items = Array.isArray(value.items) ? value.items : [];
  if (!items.length) return "";

  const texts = items
    .map((item) => smartdocToReviewString(getNestedValue(item, "content")))
    .filter(Boolean);

  if (!texts.length) return "";

  // Stored counts are authoritative when present, but a prefilled or restored value can carry
  // items without them — fall back to counting.
  const total = typeof value.total_items === "number" ? value.total_items : items.length;
  const completed =
    typeof value.completed_items === "number"
      ? value.completed_items
      : items.filter((item) => !!getNestedValue(item, "completed")).length;

  return `${texts.join(", ")} (${completed}/${total})`;
};

const getTimeFormat = (localeTimeFormat?: string): "12h" | "24h" => {
  return localeTimeFormat === "12h" ? "12h" : "24h";
};

// The review page renders a stored ISO date in the user's profile timezone (not the browser's)
// so it matches the field controls. date-fns `format` can only use the runtime's timezone, so a
// value with a time is formatted through date-fns-tz's formatInTimeZone with the profile timezone
// and the "(GMT±HH:MM)" offset (OOOO) — the same shape the Date/Due Date/Date Range controls show.
// A date-only value has no time to shift, so it keeps the plain date format.
const formatReviewDate = (
  date: string,
  includeTime: boolean,
  localeTimeFormat?: string,
  timezone?: string,
): string => {
  if (Number.isNaN(new Date(date).getTime())) return "";

  if (!includeTime) {
    return format(new Date(date), "MMMM dd, yyyy");
  }

  const timeFormatPart = getTimeFormat(localeTimeFormat) === "12h" ? "hh:mm a" : "HH:mm";
  const formatString = `MMMM dd, yyyy, ${timeFormatPart} (OOOO)`;

  return timezone
    ? formatInTimeZone(new Date(date), timezone, formatString)
    : format(new Date(date), formatString);
};

export const dateToReviewString = (
  value: unknown,
  localeTimeFormat?: string,
  timezone?: string,
): string => {
  if (!isRecord(value) || typeof value.date !== "string" || !value.date) return "";

  return formatReviewDate(value.date, !!value.include_time, localeTimeFormat, timezone);
};

export const dueDateToReviewString = (
  value: unknown,
  localeTimeFormat?: string,
  timezone?: string,
): string => {
  if (!isRecord(value)) return "";

  const fromDate = getNestedValue(value, "from_date");
  const toDate = getNestedValue(value, "to_date");
  const parts = [fromDate, toDate]
    .map((entry) => {
      if (!isRecord(entry) || typeof entry.date !== "string" || !entry.date) return "";
      return formatReviewDate(entry.date, !!entry.include_time, localeTimeFormat, timezone);
    })
    .filter(Boolean);

  return parts.join(" - ");
};

export const dateRangeToReviewString = (
  value: unknown,
  localeTimeFormat?: string,
  timezone?: string,
): string => dueDateToReviewString(value, localeTimeFormat, timezone);
